import addViewIcon from '@disy/cadenza-icons/add-view.svg';
import checkIcon from '@disy/cadenza-icons/check.svg';
import exportIcon from '@disy/cadenza-icons/export.svg';
import manageSubscriptionsIcon from '@disy/cadenza-icons/manage-subscriptions-1.svg';
import subscriptionIcon from '@disy/cadenza-icons/manage-subscriptions.svg';
import printIcon from '@disy/cadenza-icons/print.svg';
import saveIcon from '@disy/cadenza-icons/save.svg';
import settingsIcon from '@disy/cadenza-icons/settings.svg';
import shareIcon from '@disy/cadenza-icons/share.svg';
import updateIcon from '@disy/cadenza-icons/update.svg';
import { cadenzaUrl } from '@disy/lib.backend/cadenza-url';
import { isIOS } from '@disy/lib/browser';
import { logger } from '@disy/lib/logging';
import { mediaSmUp, mediaXsOnly } from '@disy/lib/media';
import { MIMETYPE_XLSX } from '@disy/lib/media-type';
import { emptyIcon, icon } from '@disy/ui/icon';
import type { MenuContentElement, MenuItem, MenuItemGroup } from '@disy/ui/menu';

import { downloadWorkbook, getWorkbookSubscriptionConfig } from 'cadenza/api-client/workbook-api';
import {
  checkIfDataViewsDataCanBeExported,
  exportMultipleDataViewData,
} from 'cadenza/api-client/workbook-export-data-api';
import { isFeatureAvailable } from 'cadenza/features';
import { getFormattedHotkey } from 'cadenza/hotkeys/hotkeys';
import { isDataViewOnDemand, isWorksheetOnDemand } from 'cadenza/map/on-demand/on-demand-map.state';
import { getStore } from 'cadenza/store/cadenza-store';
import { createTestWorkbookAction } from 'cadenza/workbook/actions/create-test-workbook-action';
import { confirmSaveWorkbookBeforeAction } from 'cadenza/workbook/confirm-save-and-execute-dialog/confirm-save-and-execute-dialog';
import { isDataViewComplete } from 'cadenza/workbook/data-view/data-view-utils';
import {
  saveAndPublishWorkbookAction,
  saveWorkbookAction,
} from 'cadenza/workbook/header/save-workbook-actions/save-workbook-actions';
import { getShareSubMenu } from 'cadenza/workbook/header/share-popup-content/share-popup-content';
import { toggleOperationModeSimplified } from 'cadenza/workbook/header/toggle-simplifed-mode/toggle-simplified-mode';
import {
  isDownloadWorkbookMenuItemVisible,
  isGenerateReportsWorkbookMenuItemVisible,
} from 'cadenza/workbook/header/workbook-header-selectors.state';
import { getShareButtonTitle } from 'cadenza/workbook/header/workbook-header-util';
import {
  canManageWorkbook,
  openManageWorkbookDialog,
} from 'cadenza/workbook/manage-workbook-dialog/manage-workbook-dialog-util';
import { isOperationModeSimplified } from 'cadenza/workbook/operation-mode';
import {
  canTheUserSaveWorkbooks,
  isSaveAndPublishButtonVisible,
  isSaveButtonVisible,
} from 'cadenza/workbook/state/workbook-header';
import { isSubscribeToWorksheetAllowed } from 'cadenza/workbook/subscribe-utils/subscribe-utils';
import type { View } from 'cadenza/workbook/view/view';
import { openVisualizationPickerModalAndAddView } from 'cadenza/workbook/workbook-add-new-view/workbook-add-new-view';
import {
  getActiveWorksheet,
  getActiveWorksheetViews,
  getDataViewsByViewId,
  getObjecttypesByDataViewId,
  getViewById,
  getWorkbookId,
  getWorkbookNameAndHash,
  getWorkbookVersion,
  getWorksheets,
  hasForceMaximizedView,
  isIntegratedView,
  isWorkbookChanged,
  isWorkbookSaved,
  isWorkbookVisibilityRestricted,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import type { ViewSettingsPanel } from 'cadenza/workbook/workbook-panel/settings/view-settings-panel/view-settings-panel';
import { WorkbookPanelName } from 'cadenza/workbook/workbook-panel/workbook-panel-utils';
import { compareViewByRowAndColumn } from 'cadenza/workbook/workbook-utils';
import { deactivateView } from 'cadenza/workbook/workbook.state';
import type { WorkbookJobScheduleConfiguration } from 'cadenza/workbook/worksheet-subscription/worksheet-subscription-wizard/workbook-job-schedule-configuration';
import { isWorksheetVisible } from 'cadenza/workbook/worksheet/worksheet-util';

import i18n from './workbook-action-menu-content.properties';
import { showPanel } from 'cadenza/workbook/state/workbook-panel-actions.state';

export function createMoreActionsMenuContent(saveAsAction: () => Promise<unknown>) {
  const items: (MenuContentElement | undefined)[] = [
    { group: [createSimplifiedOperationItem()] },
    {
      label: i18n('groupTitle.workbook'),
      group: [
        createSaveItem(),
        createSaveAndPublishItem(),
        createSaveAsItem(saveAsAction),
        createShareMenuItem(),
        createExportWorkbookItems(),
        createTestWorkbookItem(),
        createManageWorkbookItem(),
      ],
    },
    {
      label: i18n('groupTitle.worksheet'),
      group: [
        createNewViewMenuItem(),
        createRefreshCurrentWorksheetViewsMenuItem(),
        createPrintWorksheetItem(),
        createWorksheetExportItems(),
        createSubscribeWorksheetItem(),
        createManageSubscriptionsItem(),
        createQueryLogMenuItem(),
        createOpenWorksheetSettingsMenuItem(),
      ],
    },
  ];
  // Need to filter it here despite it being handled by menu, because we check if list is empty
  // in shouldShowMoreActionsButtonOnlyOnSmallDisplay
  return items.filter((item) => {
    if (item === undefined) {
      return false;
    }
    if (Object.prototype.hasOwnProperty.call(item, 'group')) {
      const groupItem = item as MenuItemGroup;
      const atLeastOneMenuContentElementIsAvailableInThisGroup = groupItem.group?.some(
        (menuContentElement) => menuContentElement !== undefined,
      );
      if (!atLeastOneMenuContentElementIsAvailableInThisGroup) {
        return false;
      }
    }
    return true;
  });
}

function createSimplifiedOperationItem(): MenuItem | undefined {
  // One of the few places where we do not check that feature based
  if (!isWorkbookVisibilityRestricted(getStore().getState())) {
    return {
      label: i18n('simplified-operation.label'),
      icon: isOperationModeSimplified(getStore().getState()) ? icon(checkIcon, { styleClass: 'success' }) : emptyIcon(),
      execute: () => toggleOperationModeSimplified(),
      hotkey: getFormattedHotkey('WORKBOOK_TOGGLE_OPERATION_MODE_SIMPLIFIED'),
    };
  }
}

function createSubscribeWorksheetItem() {
  if (isFeatureAvailable('SUBSCRIBE_TO_WORKBOOK')) {
    const { allowed, reason } = isSubscribeToWorksheetAllowed();
    return {
      label: i18n('button.subscribe-worksheet'),
      icon: icon(subscriptionIcon),
      execute: () => {
        getWorkbookSubscriptionConfig()
          .then((model) => {
            createWorksheetSubscriptionWizard(model);
          })
          .catch((error) => logger.error('Error getting subscription to worksheet wizard config ', error));
      },
      disabled: !allowed,
      tooltip: reason,
    };
  }
}

function createManageSubscriptionsItem() {
  if (isFeatureAvailable('SUBSCRIBE_TO_WORKBOOK')) {
    return {
      label: i18n('button.manage.subscriptions'),
      icon: icon(manageSubscriptionsIcon),
      url: cadenzaUrl('/subscriptions'),
    };
  }
}

function createQueryLogMenuItem() {
  if (isFeatureAvailable('WORKBOOK_PERFORMANCE_ANALYSIS')) {
    const worksheet = getActiveWorksheet(getStore().getState());
    let tooltip;
    if (isWorksheetOnDemand(worksheet)) {
      tooltip = i18n('worksheetOnDemandTooltip');
    } else if (
      worksheet.views
        .flatMap((viewId) => getDataViewsByViewId(getStore().getState(), viewId))
        .some((dataView) => isDataViewOnDemand(dataView))
    ) {
      tooltip = i18n('viewOnDemandTooltip');
    }
    return {
      label: i18n('createQueryLogMenu'),
      disabled: Boolean(tooltip),
      tooltip,
      execute: async () => {
        const { CreateQueryLogDialog } =
          await import('cadenza/workbook/create-query-log-dialog/create-query-log-dialog');
        return new CreateQueryLogDialog(getActiveWorksheet(getStore().getState()).views, true).connectAndShow();
      },
    };
  }
}

function createOpenWorksheetSettingsMenuItem() {
  if (!isWorkbookFeatureAvailable(getStore().getState(), 'workbook-management')) {
    return;
  }
  return {
    icon: icon(settingsIcon),
    label: i18n('worksheet.editSettings'),
    execute: async () => {
      getStore().dispatch(showPanel(WorkbookPanelName.VIEW_SETTINGS));
      getStore().dispatch(deactivateView());
      queueMicrotask(() => {
        document.querySelector<ViewSettingsPanel>(`.${WorkbookPanelName.VIEW_SETTINGS}`)?.updatePanelContent(true);
      });
    },
  };
}

function createRefreshCurrentWorksheetViewsMenuItem() {
  if (!isWorkbookFeatureAvailable(getStore().getState(), 'workbook-refresh-worksheet')) {
    return;
  }
  return {
    icon: icon(updateIcon),
    label: i18n('button.reload'),
    execute: () => {
      const views = document.querySelectorAll<View>('.d-worksheet-view');
      views.forEach((view) => view.reload(true));
    },
  };
}

function createPrintWorksheetItem() {
  if (mediaSmUp.matches && !isIOS && isWorkbookFeatureAvailable(getStore().getState(), 'workbook-print-worksheet')) {
    return {
      label: i18n('button.print-worksheet'),
      icon: icon(printIcon),
      execute: openPreparingPrintViewDialog,
    };
  }
}

function createWorksheetExportItems() {
  const exportReportItem = createExportWorksheetReportItem();
  const exportTableViewsItem = createExportTableViewsDataItem(true);
  if (exportReportItem || exportTableViewsItem) {
    return {
      icon: icon(exportIcon),
      label: i18n('export.menu.title'),
      subMenu: [exportReportItem, exportTableViewsItem],
    };
  }
}

function createTestWorkbookItem() {
  if (isWorkbookFeatureAvailable(getStore().getState(), 'workbook-management')) {
    const disabled = !isWorkbookSaved(getStore().getState());
    return {
      ...createTestWorkbookAction(
        getWorkbookId(getStore().getState()),
        getWorkbookVersion(getStore().getState()),
        i18n('button.testWorkbook'),
      ),
      disabled,
      tooltip: disabled ? i18n('test.workbook.disabled') : undefined,
    };
  }
}

function createManageWorkbookItem(): MenuItem | undefined {
  if (canManageWorkbook(getStore().getState())) {
    return {
      icon: icon(settingsIcon),
      label: i18n('manage-workbook.button.title'),
      execute: () => openManageWorkbookDialog(),
      hotkey: getFormattedHotkey('WORKBOOK_OPEN_MANAGE_WORKBOOK'),
    };
  }
}

async function openPreparingPrintViewDialog() {
  return import('../preparing-print-view-dialog/preparing-print-view-dialog')
    .then(({ PreparingPrintViewDialog }) => new PreparingPrintViewDialog().connectAndShow())
    .catch((error) => logger.error('failed to show PreparingPrintViewDialog', error));
}

async function openExportWorkbookDialog() {
  const { ExportWorkbookReportDialog } =
    await import('cadenza/workbook/header/export-workbook-report-dialog/export-workbook-report-dialog');
  return new ExportWorkbookReportDialog().connectAndShow();
}

function createExportWorkbookItems() {
  const isWorkbookUnsaved = !isWorkbookSaved(getStore().getState());
  const workbookUnsavedActionTooltip = isWorkbookUnsaved ? i18n('export.report.disabled') : '';
  const workbookActions: MenuItem[] = [];

  if (isDownloadWorkbookMenuItemVisible(getStore().getState())) {
    workbookActions.push({
      label: i18n('download-workbook.button.title'),
      execute: () => downloadWorkbook(getWorkbookNameAndHash(getStore().getState())),
      disabled: isWorkbookUnsaved ? true : isWorkbookChanged(getStore().getState()),
      tooltip: workbookUnsavedActionTooltip,
    });
  }
  if (isGenerateReportsWorkbookMenuItemVisible(getStore().getState())) {
    workbookActions.push({
      label: i18n('generate-reports.button.title'),
      execute: () => confirmSaveWorkbookBeforeAction(isWorkbookUnsaved, openExportWorkbookDialog),
    });
  }
  const exportTableViewsAction = createExportTableViewsDataItem(false);
  if (exportTableViewsAction) {
    workbookActions.push(exportTableViewsAction);
  }
  if (workbookActions.length) {
    return {
      icon: icon(exportIcon),
      label: i18n('export.menu.title'),
      subMenu: workbookActions,
    };
  }
}

function createNewViewMenuItem() {
  if (mediaXsOnly.matches && isWorkbookFeatureAvailable(getStore().getState(), 'workbook-new-view')) {
    return {
      icon: icon(addViewIcon),
      label: i18n('add-view-menu.button.title'),
      execute: () => openVisualizationPickerModalAndAddView(),
      disabled: hasForceMaximizedView(getStore().getState()),
    };
  }
}

function createSaveAndPublishItem() {
  if (mediaXsOnly.matches && isSaveAndPublishButtonVisible(getStore().getState())) {
    return {
      icon: icon(saveIcon),
      label: i18n('save-and-publish-workbook.button.title'),
      execute: () => saveAndPublishWorkbookAction(),
      disabled: hasForceMaximizedView(getStore().getState()) || !isWorkbookChanged(getStore().getState()),
    };
  }
}

function createSaveItem() {
  if (mediaXsOnly.matches && isSaveButtonVisible(getStore().getState())) {
    return {
      icon: icon(saveIcon),
      label: i18n('save-workbook.button.title'),
      execute: () => saveWorkbookAction(),
      disabled: hasForceMaximizedView(getStore().getState()) || !isWorkbookChanged(getStore().getState()),
    };
  }
}

function createSaveAsItem(action: () => Promise<unknown | void>) {
  if (canTheUserSaveWorkbooks(getStore().getState()) && isWorkbookSaved(getStore().getState())) {
    return {
      icon: icon(saveIcon),
      label: i18n('save-as-workbook.button.title'),
      execute: () => action(),
    };
  }
}

function createShareMenuItem() {
  if (mediaXsOnly.matches) {
    return {
      icon: icon(shareIcon),
      tooltip: getShareButtonTitle(),
      label: i18n('share.button'),
      subMenu: getShareSubMenu(),
    };
  }
}

function createWorksheetSubscriptionWizard(model: WorkbookJobScheduleConfiguration) {
  import('cadenza/workbook/worksheet-subscription/worksheet-subscription-wizard/worksheet-subscription-wizard')
    .then(({ WorksheetSubscriptionWizard }) => {
      const dialog = new WorksheetSubscriptionWizard(model);
      return dialog.connectAndShow();
    })
    .catch((error) => {
      logger.error('Error showing subscription to worksheet dialog', error);
    });
}

function createExportWorksheetReportItem() {
  if (
    isGenerateReportsWorksheetMenuItemVisible() &&
    isWorkbookFeatureAvailable(getStore().getState(), 'workbook-worksheet-pdf-export')
  ) {
    const isWorkbookUnsaved = !isWorkbookSaved(getStore().getState());
    return {
      label: i18n('generate-reports.button.title'),
      execute: () => confirmSaveWorkbookBeforeAction(isWorkbookUnsaved, openWorksheetReportDialog),
    };
  }
}

function createExportTableViewsDataItem(forWorksheet: boolean) {
  if (
    !isWorkbookFeatureAvailable(
      getStore().getState(),
      forWorksheet ? 'workbook-worksheet-tables-excel-export' : 'workbook-tables-excel-export',
    )
  ) {
    return;
  }

  const exportableDataViews = getExportableTableDataViews(forWorksheet, false);
  const disabled = exportableDataViews.length === 0;
  return {
    label: i18n('exportTableViewsData.title'),
    disabled,
    tooltip: disabled ? i18n('exportTableViewsData.noTablesAvailable') : undefined,
    execute: async () => {
      const { FileExportWithCanExportProgressDialog } =
        await import('cadenza/file-export-with-can-export-progress-dialog/file-export-with-can-export-progress-dialog');
      const dialog = new FileExportWithCanExportProgressDialog({
        title:
          forWorksheet ?
            i18n('exportTableViewsData.worksheetProgressTitle')
          : i18n('exportTableViewsData.workbookProgressTitle'),
        mediaType: MIMETYPE_XLSX,
        canExport: () =>
          checkIfDataViewsDataCanBeExported(
            getWorkbookNameAndHash(getStore().getState()),
            exportableDataViews,
            MIMETYPE_XLSX,
          ),
        startExport: (exportableDataViewIds) =>
          exportMultipleDataViewData({
            workbookNameAndHash: getWorkbookNameAndHash(getStore().getState()),
            // Maintain order and filter in all data views because of 'DATA_EXPORT' DataViewConnectionType which
            // can point to data view in different sheets which may also be hidden
            dataViewId: getExportableTableDataViews(false, true).filter((id) => exportableDataViewIds.includes(id)),
            mediaType: MIMETYPE_XLSX,
            exportType: forWorksheet ? 'worksheet' : 'workbook',
          }),
      });
      dialog.connectAndShow({ closeAll: true });
      dialog.startExportAndShowProgress();
    },
  };
}

function getExportableTableDataViews(forWorksheet: boolean, fromHiddenWorksheets: boolean) {
  const views =
    forWorksheet ?
      getActiveWorksheetViews(getStore().getState())
        .filter((view) => !isIntegratedView(getStore().getState(), view.id))
        .sort(compareViewByRowAndColumn)
      // For order of views, we need to go through this route
    : getWorksheets(getStore().getState())
        .filter((worksheet) => isWorksheetVisible(worksheet) || fromHiddenWorksheets)
        .flatMap((worksheet) =>
          worksheet.views
            .map((viewId) => getViewById(getStore().getState(), viewId))
            .filter((view) => !isIntegratedView(getStore().getState(), view.id))
            .sort(compareViewByRowAndColumn),
        );
  return views
    .filter((view) => view.type === 'TABLE')
    .flatMap((view) => view.dataViews)
    .filter((dataViewId) => {
      const objecttypes = getObjecttypesByDataViewId(getStore().getState(), dataViewId);
      return (
        objecttypes.length &&
        objecttypes.every((ot) => ot.properties?.['allow-data-export'] === 'true') &&
        isDataViewComplete(dataViewId)
      );
    });
}

function openWorksheetReportDialog() {
  const activeWorksheet = getActiveWorksheet(getStore().getState());
  return import('cadenza/workbook/worksheet/worksheet-report/worksheet-report-dialog')
    .then(({ WorksheetReportDialog }) =>
      new WorksheetReportDialog(activeWorksheet.id, activeWorksheet.printName).connectAndShow(),
    )
    .catch((e) => logger.error('Unable to open WorksheetReportDialog: ', e));
}

function isGenerateReportsWorksheetMenuItemVisible() {
  return isFeatureAvailable('CREATE_WORKBOOK_REPORT');
}
